/**
 * UTM Source Category Mapping
 *
 * Maps UTM parameters to business categories for analytics and reporting.
 *
 * Channel Definitions:
 * - Organic Social: utm_source=organic_social_media
 * - EMM (Newsletter): utm_source=EML (constant), utm_medium/utm_campaign vary
 * - Onsite BLB: utm_source=onsite, utm_medium=blb, utm_campaign=ww_maisonmytheresa
 * - Onsite PN: utm_source=app_notification, utm_medium=push, utm_campaign=ww_maisonmytheresa
 * - Onsite IAM: utm_source=app_inappmessage, utm_medium=inapp, utm_campaign=ww_maisonmytheresa
 * - Paid Social: utm_source=paid-social-upperfunnel, utm_medium=facebook
 * - SEA: utm_source=sea_nb or sea_pla, utm_medium=google
 */

/**
 * Maps UTM parameters to a business category
 * @param {Object} params - UTM parameters { source, medium, campaign, gclid, fbclid }
 * @param {string} method - How the referral was detected ('utm_params', 'referrer', 'click_id', 'unknown')
 * @returns {string} Category name
 */
export function mapUTMToCategory(params, method) {
  const source = (params.source || '').toLowerCase();
  const medium = (params.medium || '').toLowerCase();
  const campaign = (params.campaign || '').toLowerCase();
  const gclid = params.gclid || '';
  const fbclid = params.fbclid || '';

  // Click ID fallback detection (for blank referrers)
  // Google Click ID indicates SEA or Google Ads traffic
  if (gclid && !source) {
    return 'SEA';
  }
  // Facebook/Meta Click ID indicates Paid Social traffic
  if (fbclid && !source) {
    return 'Paid Social';
  }

  // Onsite Placements - Push Notifications (PN)
  if (source === 'app_notification' && medium === 'push') {
    return 'Onsite - PN';
  }

  // Onsite Placements - In-App Messages (IAM)
  if (source === 'app_inappmessage' && medium === 'inapp') {
    return 'Onsite - IAM';
  }

  // Onsite Placements - Bottom Level Banner (BLB)
  if (source === 'onsite' && medium === 'blb') {
    return 'Onsite - BLB';
  }

  // Paid Social
  if (source === 'paid-social-upperfunnel') {
    return 'Paid Social';
  }
  if (medium === 'facebook' && (campaign.includes('roe_bm') || campaign.includes('maisonmytheresa'))) {
    return 'Paid Social';
  }

  // SEA (Search Engine Advertising)
  if (source === 'sea_nb' || source === 'sea_pla') {
    return 'SEA';
  }
  if (medium === 'google' && campaign.includes('sea')) {
    return 'SEA';
  }

  // Organic Social
  if (source === 'organic_social_media') {
    return 'Organic Social';
  }

  // EMM (Newsletter) - utm_source is constant "EML"
  if (source === 'eml') {
    return 'EMM';
  }

  // Fallback based on method
  if (!source || source === 'direct') {
    return method === 'referrer' ? 'Referral' : 'Direct';
  }

  return 'Unknown';
}

/**
 * All available category names
 */
export const ALL_CATEGORIES = [
  'Paid Social',
  'SEA',
  'Organic Social',
  'EMM',
  'Onsite - BLB',
  'Onsite - PN',
  'Onsite - IAM',
  'Direct',
  'Referral',
  'Unknown',
];
